import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogsApi } from '../lib/api';
import './BlogPage.css';

interface Blog {
  id: number;
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  featuredImage?: string;
  category?: string;
  tags: string[];
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  views: number;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  author?: {
    id: number;
    name: string;
    email?: string;
  };
}

const BlogPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [relatedBlogs, setRelatedBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch the specific blog
        const blogResponse = await blogsApi.getBlog(id);
        if (blogResponse.success && blogResponse.data) {
          setBlog(blogResponse.data);

          // Fetch related blogs (published blogs in the same category)
          if (blogResponse.data.category) {
            const relatedResponse = await blogsApi.getPublishedBlogs({
              category: blogResponse.data.category,
              limit: 3
            });
            
            if (relatedResponse.success && relatedResponse.data) {
              // Filter out the current blog from related blogs
              const related = relatedResponse.data.filter((b: Blog) => b.id !== blogResponse.data.id);
              setRelatedBlogs(related);
            }
          }
        } else {
          setError('Blog post not found');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  if (loading) {
    return (
      <div className="blog-page">
        <div className="blog-loading">
          <div className="blog-loading-spinner">
            <i className="ri-loader-4-line"></i>
          </div>
          <p className="blog-loading-text">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="blog-page">
        <div className="blog-error">
          <div className="blog-error-icon">
            <i className="ri-error-warning-line"></i>
          </div>
          <div className="blog-error-content">
            <h2>Blog Post Not Found</h2>
            <p>{error || 'The requested blog post could not be found.'}</p>
            <Link to="/blogs" className="blog-back-btn">
              <i className="ri-arrow-left-line"></i>
              Back to All Blogs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      {/* Hero Section */}
      <div className="blog-hero">
        <div className="blog-hero-background">
          {blog.featuredImage && (
            <img src={blog.featuredImage} alt={blog.title} />
          )}
          <div className="blog-hero-overlay" />
        </div>
        
        <div className="container">
          <div className="blog-hero-content">
            <nav className="blog-breadcrumb">
              <Link to="/">Home</Link>
              <span className="blog-breadcrumb-separator">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <Link to="/blogs">Blogs</Link>
              <span className="blog-breadcrumb-separator">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span>{blog.category}</span>
            </nav>

            <div className="blog-hero-meta">
              <span className="blog-category">{blog.category}</span>
              <span className="blog-date">{formatDate(blog.createdAt)}</span>
            </div>

            <h1 className="blog-title">{blog.title}</h1>
            
            {blog.excerpt && (
              <p className="blog-excerpt">{blog.excerpt}</p>
            )}

            <div className="blog-author-info">
              <div className="blog-author-avatar">
                <i className="ri-user-3-line"></i>
              </div>
              <div className="blog-author-details">
                <span className="blog-author-name">{blog.author?.name || 'NUROVELI Team'}</span>
                <span className="blog-author-title">Cybersecurity Expert</span>
              </div>
              <div className="blog-stats">
                <span className="blog-views">
                  <i className="ri-eye-line"></i>
                  {blog.views || 0} views
                </span>
                <span className="blog-read-time">
                  <i className="ri-time-line"></i>
                  5 min read
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="blog-content-wrapper">
        <div className="container">
          <div className="blog-content-grid">
            {/* Article Content */}
            <article className="blog-article">
              <div className="blog-content">
                <div 
                  className="blog-text"
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />
              </div>

              {/* Tags */}
              {blog.tags && blog.tags.length > 0 && (
                <div className="blog-tags-section">
                  <h3>Tags</h3>
                  <div className="blog-tags">
                    {blog.tags.map((tag, index) => (
                      <span key={index} className="blog-tag">
                        <i className="ri-hashtag"></i>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share Section */}
              <div className="blog-share-section">
                <h3>Share this article</h3>
                <div className="blog-share-buttons">
                  <button 
                    className="blog-share-btn blog-share-twitter"
                    onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(window.location.href)}`, '_blank')}
                  >
                    <i className="ri-twitter-line"></i>
                    Twitter
                  </button>
                  <button 
                    className="blog-share-btn blog-share-linkedin"
                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                  >
                    <i className="ri-linkedin-line"></i>
                    LinkedIn
                  </button>
                  <button 
                    className="blog-share-btn blog-share-facebook"
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                  >
                    <i className="ri-facebook-line"></i>
                    Facebook
                  </button>
                  <button 
                    className="blog-share-btn blog-share-copy"
                    onClick={() => navigator.clipboard.writeText(window.location.href)}
                  >
                    <i className="ri-file-copy-line"></i>
                    Copy Link
                  </button>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="blog-sidebar">
              {/* Related Posts */}
              {relatedBlogs.length > 0 && (
                <div className="blog-sidebar-section">
                  <h3>Related Articles</h3>
                  <div className="blog-related-posts">
                    {relatedBlogs.map((relatedBlog) => (
                      <Link 
                        key={relatedBlog.id} 
                        to={`/blog/${relatedBlog.id}`}
                        className="blog-related-post"
                      >
                        {relatedBlog.featuredImage && (
                          <div className="blog-related-post-image">
                            <img src={relatedBlog.featuredImage} alt={relatedBlog.title} />
                          </div>
                        )}
                        <div className="blog-related-post-content">
                          <span className="blog-related-post-category">
                            {relatedBlog.category}
                          </span>
                          <h4 className="blog-related-post-title">
                            {truncateText(relatedBlog.title, 60)}
                          </h4>
                          <span className="blog-related-post-date">
                            {formatDate(relatedBlog.createdAt)}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Newsletter Signup */}
              <div className="blog-sidebar-section">
                <h3>Stay Updated</h3>
                <div className="blog-newsletter">
                  <p>Get the latest cybersecurity insights delivered to your inbox.</p>
                  <form className="blog-newsletter-form">
                    <input 
                      type="email" 
                      placeholder="Enter your email"
                      className="blog-newsletter-input"
                    />
                    <button type="submit" className="blog-newsletter-btn">
                      <i className="ri-mail-send-line"></i>
                      Subscribe
                    </button>
                  </form>
                </div>
              </div>

              {/* Quick Links */}
              <div className="blog-sidebar-section">
                <h3>Quick Links</h3>
                <div className="blog-quick-links">
                  <Link to="/dashboard" className="blog-quick-link">
                    <i className="ri-dashboard-line"></i>
                    Security Dashboard
                  </Link>
                  <Link to="/register" className="blog-quick-link">
                    <i className="ri-shield-check-line"></i>
                    Start Monitoring
                  </Link>
                  <Link to="/#services" className="blog-quick-link">
                    <i className="ri-service-line"></i>
                    Our Services
                  </Link>
                  <Link to="/#contact" className="blog-quick-link">
                    <i className="ri-customer-service-line"></i>
                    Contact Support
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="blog-cta-section">
        <div className="container">
          <div className="blog-cta">
            <div className="blog-cta-content">
              <h3>Ready to Secure Your Digital Assets?</h3>
              <p>
                Join thousands of organizations that trust NUROVELI for 
                advanced threat detection and AI-powered security monitoring.
              </p>
            </div>
            <div className="blog-cta-actions">
              <Link to="/register" className="blog-cta-btn blog-cta-btn-primary">
                <span>Start Free Trial</span>
                <i className="ri-rocket-line"></i>
              </Link>
              <Link to="/#contact" className="blog-cta-btn blog-cta-btn-secondary">
                <span>Talk to Expert</span>
                <i className="ri-chat-3-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;